import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { truncateName } from '@eika-infrastruktur/format';
import { AttentionMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';
import { Label } from '@eika/label';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import { useConfig } from '../../../defaultConfig';
import { avtalegiroSelector, paymentFromSelector, useAccounts } from '../../../fetchHooks/useAccount';
import type { EFaktura, EfakturaType } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import { useFormContext } from '../../../hooks/useForm';
import { useReceiver } from '../../../hooks/useSetReceiver';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { ANALYTICS_END, ANALYTICS_START, AnalyticsEvents, trackEvent } from '../../../services/analytics';
import SigningFlowWithConfirmation from '../../../signingContext/SigningFlowWithConfirmation';
import { useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EfakturaLink } from '../../EfakturaLink';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import CenteredModalContent from '../../shared/CenteredModalContent/CenteredModalContent';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import EfakturaInvalidAccountInfo from '../EfakturaInvalidAccountInfo';
import { PaymentError } from '../Payment/form/validate';
import useSetFromAccountIfUndefined from '../Payment/hooks/useSetFromAccountIfUndefined';
import { getRedirectLinkAvtalegiro } from '../Payment/mapper';
import { PaymentAccount } from '../PaymentAccount';
import { PaymentRetryContext } from '../PaymentRetry';
import TransactionResultInfo from '../TransactionResultInfo';
import DeleteEFaktura, { DeletionStatus } from './DeleteEFaktura';
import EFakturaFields from './EFakturaFields';
import createEfaktura from './createEfaktura';
import { PaymentAction, PaymentActionType } from './form/actions';
import { EfakturaState } from './form/state';

import '../TransactionModal.scss';
import './EFaktura.scss';

const approvableTypes: EfakturaType[] = [
    'EFAKTURA',
    'ENFORCEMENT_WARNING',
    'COLLECTION_NOTICE',
    'REMINDER',
    'DUNNING',
    'PAYMENT_REQUEST',
];

export interface Props {
    account?: PaymentAccount;
    onClose: () => void;
    onDelete: () => void;
    eFaktura: EFaktura;
}

const EFakturaForm: React.FC<Props> = ({ eFaktura, onDelete, account, onClose }) => {
    const translated = useTranslation();
    const { avtalegiroLink } = useConfig();
    const [deletionState, setDeletionState] = useState(DeletionStatus.INITIAL);
    const { targetFromAccount } = usePaymentAccountContext();
    const { state, handleChange, checkFormValidity } = useFormContext<EfakturaState, PaymentAction, PaymentError>();

    const {
        signingOperationPending,
        signingError,
        signingStatus,
        showSigningFlow,
        handleCancelSigningFlow,
        startSigning,
        dispatch,
        showPaymentRetry,
    } = useSigningContext(createEfaktura());

    useEffect(() => {
        if (eFaktura.id.value !== state.efakturaId?.value) {
            handleChange({ type: PaymentActionType.INIT_FORM, payload: { eFaktura } });
        }
    }, [eFaktura, handleChange, state.efakturaId?.value]);

    const selectedReceiver = useReceiver(state.toAccount?.accountNumber);
    useEffect(() => {
        if (selectedReceiver) {
            handleChange({
                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                payload: selectedReceiver,
            });
        }
    }, [handleChange, selectedReceiver]);

    const { data: accounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const { data: avtalegiroAccounts = [] } = useAccounts({ select: avtalegiroSelector });

    useEffect(() => {
        trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, ANALYTICS_START);
        return () => trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, ANALYTICS_END);
    }, []);

    const redirectLinkAvtaleGiro = useMemo(
        () => getRedirectLinkAvtalegiro(state, targetFromAccount, avtalegiroAccounts.length > 0, avtalegiroLink),
        [state, targetFromAccount, avtalegiroAccounts.length, avtalegiroLink],
    );
    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
                trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'create_efaktura_payment');
            } else {
                trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'validation_error');
            }
        },
        [startSigning, checkFormValidity, state],
    );

    const createPaymentWithDuplicateOverride = useCallback(() => {
        trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'create_efaktura_payment', 'duplicate');
        startSigning({ ...state, acceptDuplicate: true });
    }, [startSigning, state]);

    const fromAccountId = useSetFromAccountIfUndefined<EfakturaState>(
        state,
        (account) =>
            handleChange({
                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                payload: account.accountNumber,
            }),
        account,
        true,
    );

    const showPaymentResult = useShowPaymentResult();

    const closeAndCancelSigning = () => {
        handleCancelSigningFlow();
        onClose();
    };

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    if (accountState.isFetched && !accounts?.length) {
        return <EfakturaInvalidAccountInfo okButton={onClose} />;
    }
    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return (
            <TransactionResultInfo
                type="EFAKTURA"
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId}
                onClose={onClose}
                onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
                onContinue={() => {
                    dispatch({ type: Actions.CLEAR_ERROR });
                    createPaymentWithDuplicateOverride();
                }}
                paymentDate={state.paymentDate}
                redirectLinkAvtalegiro={redirectLinkAvtaleGiro}
            />
        );
    }

    if (
        deletionState === DeletionStatus.LOADING ||
        deletionState === DeletionStatus.CONFIRMING ||
        deletionState === DeletionStatus.RESULT
    ) {
        return (
            <CenteredModalContent withMessage>
                <DeleteEFaktura
                    eFaktura={eFaktura}
                    onDelete={onDelete}
                    deletionState={deletionState}
                    setStatus={setDeletionState}
                />
            </CenteredModalContent>
        );
    }

    const canApprove = approvableTypes.includes(eFaktura.efakturaType) && eFaktura.permission?.canEdit;
    const canDelete = eFaktura.permission?.canReject;

    return (
        <SigningFlowWithConfirmation
            enable={!!eFaktura.owner?.name}
            confirmText={translated.eFaktura.confirmPayment}
            labelText={format(translated.approvalItem.sharedBy, eFaktura.owner?.name ?? '')}
            title={translated.eFaktura.confirmationDialogTitle}
            id="efaktura-payment-confirmation-dialog"
            onClosePayment={closeAndCancelSigning}
        >
            <div className={classNames('e-faktura__modal-header-wrapper', { 'with-owner': !!eFaktura.owner?.name })}>
                <div className="e-faktura__modal-header-content">
                    <h2 className="e-faktura__modal-header">{translated.eFaktura.eFaktura}</h2>
                    {eFaktura.owner?.name && (
                        <Label
                            type="warning"
                            size={isMobile ? 'small' : 'standard'}
                            text={format(translated.approvalItem.sharedBy, truncateName(eFaktura.owner.name))}
                        />
                    )}
                </div>

                {!isMobile && <EfakturaLink eFakturaId={eFaktura.id} disabled={signingOperationPending} />}
            </div>

            <TransactionForm onSubmit={onSubmit}>
                {translated.eFaktura.notice[eFaktura.efakturaType] && (
                    <AttentionMessage
                        className="e-faktura-attention-notice"
                        type="attention"
                        icon={IconTypes.INFO_20}
                        text={translated.eFaktura.notice[eFaktura.efakturaType]}
                    />
                )}
                <EFakturaFields locked={!canApprove || signingOperationPending || showSigningFlow} />

                {isMobile && (
                    <EfakturaLink
                        className="e-faktura-body__view-e-faktura "
                        eFakturaId={eFaktura.id}
                        disabled={signingOperationPending || showSigningFlow}
                    />
                )}

                <TransactionSubmitWrapper className="e-faktura-body__button-row">
                    {canApprove && (
                        <ButtonWithSpinner
                            className="efaktura-body__submit"
                            buttonType="positive"
                            type="submit"
                            data-testid="payment-button"
                            showSpinner={signingOperationPending || showSigningFlow}
                        >
                            {translated.payWithBankId}
                        </ButtonWithSpinner>
                    )}
                    {canDelete && (
                        <LinkButton
                            disabled={signingOperationPending || showSigningFlow}
                            className="e-faktura-body__delete"
                            icon={IconTypes.SLETT_24}
                            onClick={() => setDeletionState(DeletionStatus.CONFIRMING)}
                        >
                            {translated.eFaktura.delete}
                        </LinkButton>
                    )}
                </TransactionSubmitWrapper>
            </TransactionForm>
            <DismissKeyboardStickyFooter />
        </SigningFlowWithConfirmation>
    );
};

export default EFakturaForm;
